import { capturarEvento } from '@titularis/analytics';

/**
 * Os eventos da landing, e até 17/09/2026 não havia nenhum.
 *
 * O site media só a tela - o pageview automático do PostHog - e nada mais. Isso
 * dá o denominador do funil e esconde os dois numeradores que importam: **a
 * inscrição na lista**, que é a conversão da própria landing, e **a saída para
 * o app**, que é onde a landing entrega o visitante e onde ela pode estar
 * perdendo gente sem que nada acuse.
 *
 * Com o GTM começando em grupo, Instagram e busca, esses dois números são o que
 * separa "o canal não traz ninguém" de "o canal traz e a página não converte" -
 * e as duas conclusões levam a trabalhos opostos.
 *
 * Nomes em inglês, como os do `apps/app`, para os dois funis serem legíveis no
 * mesmo painel.
 */
export type EventoDoSite =
  /**
   * A saída para o app: clique em qualquer porta do funil.
   *
   * `destino` é o caminho no app (`/criar-conta`, `/entrar`), que é o que
   * separa a porta larga da estreita; `rotulo` é o texto do botão, que é o que
   * diz **de onde na página** o clique partiu sem precisar de um identificador
   * por seção que alguém teria de manter.
   */
  | { nome: 'cta_clicked'; destino: string; rotulo: string }
  /**
   * A inscrição na lista de espera, depois de o servidor aceitar.
   *
   * Depois, e não no envio: o que interessa é a inscrição que existe, e contar
   * a tentativa que falhou inflaria a conversão justamente quando algo está
   * quebrado.
   */
  | { nome: 'lead_submitted'; topic: string; origem: string }
  /**
   * O PDF levado para casa.
   *
   * **É a isca da Fase 1 e ninguém sabia se alguém baixava.** O banco conta em
   * `exam_file_events`, exato e sem atribuição nenhuma: o número existe e não se
   * quebra por canal, que é justamente a pergunta de quem posta o link num
   * grupo. O plano previa isto como `paper_downloaded`; o nome saiu mais largo
   * porque o mesmo leitor serve a prova e o artigo da Revista, e um evento
   * chamado `paper` para um PDF de revista seria mentira num relatório.
   */
  | { nome: 'pdf_downloaded'; tipo: TipoDeDocumento; documento: string; arquivo?: string }
  /**
   * O muro: a pessoa clicou em baixar e recebeu o convite para criar conta.
   *
   * **É o degrau mais alto do funil de busca e de grupo, e era invisível.** Ler
   * e imprimir é livre, baixar pede conta - então este é o instante em que uma
   * visita anônima decide se vira cadastro. O clique de saída já era medido pelo
   * `cta_clicked`, que apanha qualquer âncora para o app; o que faltava era o
   * denominador, sem o qual a taxa não existe.
   */
  | { nome: 'pdf_blocked'; tipo: TipoDeDocumento; documento: string; arquivo?: string }
  /**
   * O botão "Enviar no WhatsApp" da página de um material.
   *
   * O clique é o que o site enxerga; o envio acontece no aplicativo, fora
   * daqui. O que volta dele é a visita marcada com
   * `utm_medium=compartilhamento`, e a razão entre as duas é a taxa de quem
   * chegou a mandar.
   */
  | { nome: 'share_clicked'; canal: 'whatsapp'; tipo: TipoDeDocumento; documento: string };

/** De que página o PDF saiu. É o que separa a isca de prova da da Revista e do material. */
export type TipoDeDocumento = 'prova' | 'revista' | 'material';

/**
 * O documento que o leitor está servindo, para os eventos terem sujeito.
 *
 * Obrigatório de propósito: o leitor serve a três páginas hoje, e a quarta
 * não pode nascer muda por esquecimento - sem este objeto ela não compila.
 */
export interface DocumentoDoLeitor {
  tipo: TipoDeDocumento;
  /** O slug da prova ou do artigo, que é o que identifica a peça no relatório. */
  documento: string;
  /** `exam`, `answer-key` ou `edital`. Ausente quando o documento é um só. */
  arquivo?: string;
}

export function registrarEvento(evento: EventoDoSite): void {
  const { nome, ...propriedades } = evento;
  capturarEvento(nome, propriedades);
}

/**
 * O que um clique em âncora significa para o funil, ou nada.
 *
 * Pura e exportada porque é a única decisão do ouvinte abaixo, e a que erra
 * calado: reconhecer link demais enche o relatório de navegação interna,
 * reconhecer de menos apaga a porta que mais converte. O ouvinte que a usa é um
 * `addEventListener` e não tem o que provar.
 */
type SaidaDoFunil = Extract<EventoDoSite, { nome: 'cta_clicked' }>;

export function saidaParaOApp(
  ancora: { href: string; textContent: string | null },
  app: string,
): SaidaDoFunil | null {
  let destino: URL;
  let base: URL;

  /*
   * **Origem, e não prefixo.** A primeira versão comparava com `startsWith`, e
   * o próprio teste a pegou: `app.titularis.com.br.exemplo.com` começa com a
   * base do app e entrava no funil. Prefixo de texto não sabe onde o host
   * termina; `origin` sabe, e ainda descarta de graça o que não é página -
   * `mailto:` tem origem nula e nunca casa.
   */
  try {
    destino = new URL(ancora.href);
    base = new URL(app);
  } catch {
    return null;
  }

  if (destino.origin !== base.origin) return null;

  return {
    nome: 'cta_clicked',
    destino: destino.pathname,
    // O texto é o rótulo que a pessoa leu; cortado porque não é um texto.
    rotulo: (ancora.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 80) || '(sem texto)',
  };
}

/**
 * Um ouvinte só para todos os CTAs, e é de propósito.
 *
 * As portas para o app aparecem em dezessete lugares - cabeçalho, herói,
 * módulos, planos, fechamento, 404, leitor de PDF, e o convite que sai depois
 * da inscrição na lista. Instrumentar um por um significaria dezessete pontos
 * para alguém esquecer, e o décimo oitavo nasceria mudo: o relatório continuaria
 * existindo, com um número menor, e ninguém perceberia.
 *
 * Aqui a regra é uma: **âncora que aponta para o app é saída do funil**. Quem
 * adicionar um botão novo amanhã ganha a medição sem saber que ela existe.
 *
 * Na fase de captura porque o clique navega: na fase de bolha o documento já
 * pode estar sendo descarregado. O `capture` do PostHog não espera resposta, e
 * é isso que faz o evento sobreviver à navegação.
 */
export function observarSaidasParaOApp(app: string): void {
  document.addEventListener(
    'click',
    (evento) => {
      const alvo = evento.target;
      if (!(alvo instanceof Element)) return;

      const ancora = alvo.closest('a[href]');
      if (!(ancora instanceof HTMLAnchorElement)) return;

      const saida = saidaParaOApp(ancora, app);
      if (saida) registrarEvento(saida);
    },
    { capture: true },
  );
}
